import { ModifierProperty } from '@/enums';
import { useGetComponentConfigByModule } from '@/hooks';
import {
  ControlledInput,
  ControlledInputProps,
} from '@amili-v3/amili-v3-design';
import { forwardRef, memo, useEffect, useMemo, useRef, useState } from 'react';
import { transformOptionsByConfig } from './utils';
import classNames from 'classnames';
import { ExpandControlledConfigProps } from '@/types';

const ControlledInputConfig = forwardRef<
  HTMLDivElement,
  ExpandControlledConfigProps
>((props, ref) => {
  const {
    isPreventCustomConfig = false,
    options,
    required: originRequired,
    disabled: originDisabled,
    className,
    id,
    ...rest
  } = props;
  const divRef = useRef<HTMLDivElement>(null);

  // TODO: Remember to set name (id) for each form = form id
  const [formId, setFormId] = useState('');
  useEffect(() => {
    if (divRef.current) {
      const formElement = divRef.current?.closest('form');
      setFormId(formElement?.id ?? '');
    }
  }, []);

  const fieldName = id ?? props.name.split('.').at(-1) ?? '';
  const customFieldKey = formId ? `${fieldName}-${formId}-input` : '';
  const fieldNameConfig = useGetComponentConfigByModule('form', formId);

  const { disabled, hidden, required } = useMemo(() => {
    const modifier = fieldNameConfig[customFieldKey]?.modifier;

    return {
      disabled: modifier === ModifierProperty.DISABLED,
      hidden: modifier === ModifierProperty.HIDDEN,
      required: modifier === ModifierProperty.MANDATORY,
      optional: modifier === ModifierProperty.OPTIONAL,
    };
  }, [fieldNameConfig, customFieldKey]);

  const customOptions = transformOptionsByConfig({
    inputType: props?.inputType,
    customKey: customFieldKey,
    dataConfig: fieldNameConfig,
    options,
    isPreventCustomConfig,
  });

  if (hidden) return null;
  return (
    <div
      ref={ref ?? divRef}
      data-customization-key={customFieldKey}
      className={classNames(['customized-input-container', className])}
    >
      <ControlledInput
        {...rest}
        id={id}
        className={className}
        data-customization-key={customFieldKey}
        disabled={disabled || originDisabled}
        // required={optional ? !optional : required || originRequired} //disabled field optional
        required={required || originRequired}
        options={customOptions as ControlledInputProps['options']}
      />
    </div>
  );
});

ControlledInputConfig.displayName = 'ControlledInputConfig';
export default memo(ControlledInputConfig);
